import { TIER_PRICE_ABI } from '@lfg/abis';
import { Environment } from '@lfg/core/enums';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback, useState } from 'react';
import { Address } from 'viem';
import { bsc, bscTestnet } from 'viem/chains';
import { useAccount } from 'wagmi';
import { readContract } from 'wagmi/actions';

import { apiClientV2 } from '@/agent/apiClient/api';
import { APP_ENV } from '@/global/configs/common.config';
import { USD_VALUE_DECIMAL } from '@/global/configs/decimal.config';
import type { GetPriceFeedProps, GetPriceInUSDProps, PriceFeed, PriceV1Hook } from '@/hooks/types/price.type';
import { useFetchPricesV2Mutation } from '@/store/services/price.service';
import { useAppSelector } from '@/store/utils';

import { useWeb3 } from '../useWeb3';

const INITIAL_PRICE_FEED: PriceFeed = {
  price: 0,
  prices: { bsc: 0, lix: 0, usdt: 0, usdc: 0 },
  original: null,
};

const usePriceV2 = (): PriceV1Hook => {
  const { address } = useAccount();
  const { config } = useWeb3();
  const { nodeId, referralCode, address: receivedAddress } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);
  const [priceFeed, setPriceFeed] = useState<PriceFeed>(INITIAL_PRICE_FEED);
  const [priceInUsd, setPriceInUsd] = useState<number>(0);
  const [needToSign, setNeedToSign] = useState<boolean>(false);
  const [fetchPricesV2] = useFetchPricesV2Mutation();
  const {
    value: isFetchingPriceInUSD,
    setTrue: setIsFetchingPriceInUSDTrue,
    setFalse: setIsFetchingPriceInUSDFalse,
  } = useBoolean(false);

  const {
    value: isFetchingPriceFeed,
    setTrue: setIsFetchingPriceFeedTrue,
    setFalse: setIsFetchingPriceFeedFalse,
  } = useBoolean(false);

  const getPriceInUSD = useCallback(
    async ({
      amount,
      currentTierPrice = 0,
      isLoadingAgain = true,
    }: GetPriceInUSDProps): Promise<number | undefined> => {
      if (!nodeId || !projectInfo?.tier?.contract) return;
      isLoadingAgain && setIsFetchingPriceInUSDTrue();
      let price = (currentTierPrice * amount) / 10 ** USD_VALUE_DECIMAL;

      try {
        const data = (await readContract(config, {
          abi: TIER_PRICE_ABI,
          address: projectInfo.tier.contract as Address,
          functionName: 'getUSDValue',
          args: [amount],
          chainId: APP_ENV !== Environment.PROD ? bscTestnet.id : bsc.id,
        })) as [bigint, number];

        price = Number(data[0]) / 10 ** data[1];
        isLoadingAgain && setPriceInUsd(price);
        return price;
      } catch (error) {
        console.error({ error });
        isLoadingAgain && setPriceInUsd(price);
        return price;
      } finally {
        setIsFetchingPriceInUSDFalse();
      }
    },
    [nodeId, address, config, projectInfo],
  );

  const getPriceFeed = useCallback(
    async ({ coin, nft, amount = 1 }: GetPriceFeedProps & { nft?: string }) => {
      setIsFetchingPriceFeedTrue();
      let price = INITIAL_PRICE_FEED;

      try {
        const priceData = await fetchPricesV2({
          amount,
          buyerAddress: receivedAddress !== '' ? receivedAddress : address,
          coin,
          nft: nft ?? nodeId,
          ...(referralCode !== '' && { referralCode }),
        });

        if (priceData.data) {
          price = {
            price: ((priceData.data.price ?? 0) * amount) / 10 ** priceData.data.decimals,
            prices: priceData.data.prices,
            original: priceData.data,
          };
        }

        setPriceFeed(price);
        return price;
      } catch (error: any) {
        setPriceFeed(INITIAL_PRICE_FEED);
        throw error;
      } finally {
        setIsFetchingPriceFeedFalse();
      }
    },
    [address, nodeId, receivedAddress, referralCode, fetchPricesV2],
  );

  const checkRegisterOrNot = useCallback(
    async ({ coin, amount = 1 }: GetPriceFeedProps) => {
      try {
        const response = await apiClientV2.post(
          `v1/prices`,
          { coin, sale: nodeId, amount },
          { headers: { 'x-offchain-address': address } },
        );
        if (!response.data.data?.haveReferral && !response.data.data?.signature) {
          setNeedToSign(true);
        } else {
          setNeedToSign(false);
        }
      } catch (error: any) {
        if (error.response?.data?.code === '5000') {
          setNeedToSign(true);
        } else {
          throw error;
        }
      }
    },
    [address, nodeId],
  );

  return {
    needToSign,
    priceFeed,
    priceInUsd,
    isFetchingPriceFeed,
    isFetchingPriceInUSD,
    getPriceFeed,
    getPriceInUSD,
    setNeedToSign,
    checkRegisterOrNot,
  };
};

export { usePriceV2 };
